import { useId, useRef, useState } from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, Chip, IconButton, Popover, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';

export interface HelpPopoverProps {
  /** Short scope line, rendered first and visually distinct. */
  scope: string;
  /** The long-form explanation. */
  body: string;
  /** Accessible name — the setting's label. */
  label: string;
}

export function HelpPopover({ scope, body, label }: HelpPopoverProps) {
  const { t } = useCopy();
  const popoverId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const close = () => {
    setAnchorEl(null);
    triggerRef.current?.focus();
  };

  return (
    <>
      <IconButton
        ref={triggerRef}
        type="button"
        size="small"
        aria-label={t('help.ariaLabel', 'Help for {{label}}', { label })}
        aria-describedby={open ? popoverId : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ maxWidth: 440, p: 2 }}>
          <Chip label={scope} size="small" sx={{ mb: 1 }} />
          <Typography variant="body2">{body}</Typography>
        </Box>
      </Popover>
    </>
  );
}
